import { CancelIcon } from '@/assets/icons'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import { EventCategory } from '@orbit/api-types'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import {
    FormGroupComponent,
    FormInputComponent,
    FormSelectComponent,
    FormTextareaComponent,
} from './FormComponents'
import {
	AddLinkButton,
	LinkInput,
	LinkInputWrapper,
	LinksListWrapper,
	LocationSecondaryText,
	RemoveLinkButton,
	TimePickerWrapper,
} from './FormComponents.styles'

const EVENT_CATEGORIES: EventCategory[] = [
	'FoodAndDrink',
	'SeeAndDo',
	'Nature',
	'Shopping',
	'Accommodation',
	'Transportation',
	'Event',
	'Other',
]

const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Events',
	Other: 'Other',
}

interface EventFormProps {
	formId: string
	title: string
	description: string
	category: EventCategory | undefined
	locationPrimaryText: string
	locationSecondaryText?: string
	links: string[]
	startTime?: string | null
	onTitleChange: (title: string) => void
	onDescriptionChange: (description: string) => void
	onCategoryChange: (category: EventCategory | undefined) => void
	onLocationPrimaryTextChange: (locationPrimaryText: string) => void
	onLocationSelect: (place: PlaceSuggestion) => void
	onLinksChange: (links: string[]) => void
	onStartTimeChange: (startTime: string) => void
	onSubmit: (e: React.FormEvent) => void
	attachmentSlot?: React.ReactNode
}

const EventForm: React.FC<EventFormProps> = ({
	formId,
	title,
	description,
	category,
	locationPrimaryText,
	locationSecondaryText,
	links,
	startTime,
	onTitleChange,
	onDescriptionChange,
	onCategoryChange,
	onLocationPrimaryTextChange,
	onLocationSelect,
	onLinksChange,
	onStartTimeChange,
	onSubmit,
	attachmentSlot,
}) => {
	const handleLinkChange = (index: number, value: string) => {
		const newLinks = [...links]
		newLinks[index] = value
		onLinksChange(newLinks)
	}

	const handleAddLink = () => {
		onLinksChange([...links, ''])
	}

	const handleRemoveLink = (index: number) => {
		const newLinks = links.filter((_, i) => i !== index)
		onLinksChange(newLinks)
	}

	return (
		<form id={formId} onSubmit={onSubmit}>
		<FormGroupComponent label="Location (Optional)" htmlFor="event-location">
			<LocationAutocomplete
				id="event-location"
				value={locationPrimaryText}
				onChange={onLocationPrimaryTextChange}
				onPlaceSelect={onLocationSelect}
				placeholder="Search for a location..."
				autoFocus
			/>
			{locationSecondaryText && (
				<LocationSecondaryText>
					{locationSecondaryText}
				</LocationSecondaryText>
			)}
		</FormGroupComponent>
			<FormInputComponent
				label="Event Title"
				htmlFor="event-title"
				id="event-title"
				type="text"
				value={title}
				onChange={e => onTitleChange(e.target.value)}
				placeholder="Enter event title..."
				required
			/>
			<FormSelectComponent
				label="Category"
				htmlFor="event-category"
				id="event-category"
				value={category || EVENT_CATEGORIES[0]}
				onChange={e => onCategoryChange(e.target.value as EventCategory)}
			>
				{EVENT_CATEGORIES.map(cat => (
					<option key={cat} value={cat}>
						{EVENT_CATEGORY_LABELS[cat]}
					</option>
				))}
			</FormSelectComponent>
			<FormGroupComponent label="Start Time (Optional)" htmlFor="event-start-time">
				<TimePickerWrapper>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TimePicker
							value={startTime ? dayjs(`2000-01-01T${startTime}`) : null}
							onChange={(newValue: Dayjs | null) => {
								if (newValue) {
									onStartTimeChange(newValue.format('HH:mm'))
								} else {
									onStartTimeChange('')
								}
							}}
							format="HH:mm"
							slotProps={{
								textField: {
									placeholder: 'Select time',
									fullWidth: true,
								},
							}}
						/>
					</LocalizationProvider>
				</TimePickerWrapper>
			</FormGroupComponent>
			<FormTextareaComponent
				label="Description (Optional)"
				htmlFor="event-description"
				id="event-description"
				value={description}
				onChange={e => onDescriptionChange(e.target.value)}
				placeholder="Enter event description..."
				rows={3}
			/>
			<FormGroupComponent label="Links (Optional)" htmlFor="event-links">
				{links.length > 0 && (
					<LinksListWrapper>
						{links.map((link, index) => (
							<LinkInputWrapper key={index}>
								<LinkInput
									type="text"
									placeholder="Enter a link (e.g., https://example.com)"
									value={link}
									onChange={e => handleLinkChange(index, e.target.value)}
									autoComplete="off"
								/>
								<RemoveLinkButton
									type="button"
									onClick={() => handleRemoveLink(index)}
									title="Remove link"
								>
									<CancelIcon sx={{ fontSize: 18 }} />
								</RemoveLinkButton>
							</LinkInputWrapper>
						))}
					</LinksListWrapper>
				)}
				<AddLinkButton
					type="button"
					onClick={handleAddLink}
				>
					+ Add Link
				</AddLinkButton>
			</FormGroupComponent>
			{attachmentSlot}
		</form>
	)
}

export default EventForm
